import { createAsyncThunk, createSlice, } from "@reduxjs/toolkit";
import * as api from "@repo/shared/api";
import { assetUrl } from "../../utils/assets";
import {} from "../store";
const PLAYER_PICK_ASSET = assetUrl("fut23/misc/playerpick.png");
export const extractRewardUrl = (reward) => {
    if (!reward) {
        return "";
    }
    const defaultRewardImage = reward.rewardImgURL || reward.packImageURL || "";
    switch (reward.type) {
        case "playerpick":
            return PLAYER_PICK_ASSET;
        default:
            return defaultRewardImage;
    }
};
export const initialFiltersState = {
    playersFrom: "market",
    prioritizeDuplicates: false,
    includeTransferlist: false,
    prioritizeSbcStorage: false,
    useUntradeablesOnly: false,
    excludeActiveSquad: true,
    excludeSbcPlayers: false,
    excludeObjectivePlayers: false,
    includedPlayers: [],
    excludedPlayers: [],
    minRating: null,
    maxRating: null,
};
// Initial state
const initialState = {
    sbcSets: [],
    filters: initialFiltersState,
    sbcSetsStatus: "idle",
    openSbcSetStatus: "idle",
    solveSbcStatus: "idle",
    solutions: [],
    selectedSolutionId: null,
    solutionSbcId: null,
    playerSwapsStatus: "idle",
    playerSwaps: [],
};
// fetch sbc sets
export const fetchSbcSets = createAsyncThunk("sbc/fetchSbcSets", async () => {
    const response = await api.getSbcSets();
    return response;
});
export const fetchSbcs = createAsyncThunk("sbc/fetchSbcs", async ({ sbcSetId, platform }) => {
    const response = await api.getSbcs(sbcSetId, platform);
    return response;
});
/** One set, for a page reached without the list having been loaded. */
export const fetchSbcSet = createAsyncThunk("sbc/fetchSbcSet", async (sbcSetId) => api.getSbcSet(sbcSetId));
/**
 * Everything a set page needs, in the order it needs it.
 *
 * The sets list is only filled by the SBCs screen, but a set page can be opened
 * straight from a front-page card or a deep link. `fetchSbcs` maps over the sets
 * already in the slice, so with the list empty its result is dropped on the
 * floor and the page reports "No SBC Set Found" — the set has to land first.
 */
export const openSbcSet = createAsyncThunk("sbc/openSbcSet", async ({ sbcSetId, platform }, { dispatch, getState }) => {
    const known = getState().sbcSetsReducer.sbcSets.some((sbcSet) => sbcSet.id === sbcSetId);
    if (!known)
        await dispatch(fetchSbcSet(sbcSetId)).unwrap();
    await dispatch(fetchSbcs({ sbcSetId, platform })).unwrap();
});
export const markSbcSolved = createAsyncThunk("mark-sbc-solved", async ({ sbcId, setId, solved, }, { dispatch }) => {
    // dispatch(updateSbcSolved(sbcId));
    // // Notify sbcSetsSlice that one more/less sbc has been solved
    // dispatch(updateSbcSolveInSets({ setId, solved, sbcId }));
    await api.markSbcSolved(sbcId, setId, solved);
    return { sbcId, setId, solved };
});
/*
  The four things done to a set rather than inside it. Each writes the new state
  first and posts after: the control is a toggle the user is watching, and a
  round trip's worth of lag on it reads as a dropped tap.
*/
export const markSbcSetAsFavorite = createAsyncThunk("sbc/markSbcSetAsFavorite", async ({ setId, isFavorite }, { dispatch }) => {
    dispatch(sbcSetFavorited({ setId, isFavorite }));
    try {
        await api.markSbcSetAsFavorite(setId, isFavorite);
    }
    catch (err) {
        // Shown before the request: put it back if the request failed.
        dispatch(sbcSetFavorited({ setId, isFavorite: !isFavorite }));
        throw err;
    }
});
export const hideSbcSet = createAsyncThunk("sbc/hideSbcSet", async ({ setId, isHidden }, { dispatch }) => {
    dispatch(sbcSetHidden({ setId, isHidden }));
    try {
        await api.hideSbcSet(setId, isHidden);
    }
    catch (err) {
        dispatch(sbcSetHidden({ setId, isHidden: !isHidden }));
        throw err;
    }
});
export const markSbcSetSolved = createAsyncThunk("sbc/markSbcSetSolved", async ({ setId, didComplete }, { dispatch }) => {
    dispatch(sbcSetCompleted({ setId, didComplete }));
    try {
        await api.markSbcSetSolved(setId, didComplete);
    }
    catch (err) {
        dispatch(sbcSetCompleted({ setId, didComplete: !didComplete }));
        throw err;
    }
});
/**
 * One tap on an arrow. Casting the vote you already hold takes it back, so the
 * caller says which arrow was pressed and this works out whether that is a vote
 * or an undo.
 */
export const voteSbcSet = createAsyncThunk("sbc/voteSbcSet", async ({ setId, vote }, { dispatch, getState }) => {
    const sbcSet = getState().sbcSetsReducer.sbcSets.find((set) => set.id === setId);
    const held = vote === "UPVOTE" ? sbcSet?.wasUpvoted : sbcSet?.wasDownvoted;
    dispatch(sbcSetVoted({ setId, vote }));
    if (held)
        await api.undoVoteSbcSet(setId, vote);
    else
        await api.voteSbcSet(setId, vote);
});
export const playerSwap = createAsyncThunk("player-swap", async ({ id, solutionPlayers }, { getState }) => {
    const { sbcSetsReducer } = getState();
    const { playersFrom, useUntradeablesOnly, excludeActiveSquad, excludeObjectivePlayers, excludeSbcPlayers, includeTransferlist: includeTransferList, prioritizeDuplicates, prioritizeSbcStorage, minRating, maxRating, } = sbcSetsReducer.filters;
    const { excludedPlayers } = sbcSetsReducer.filters;
    const excludedPlayersMapped = excludedPlayers.flatMap((p) => p ? p.resourceId : []);
    const playerIds = (solutionPlayers || []).map((p) => p.resource_id);
    return await api.playerSwap({
        id,
        untradeable: useUntradeablesOnly,
        excludeActiveSquad,
        excludeObjectivePlayers,
        excludeSbcPlayers,
        includeTransferList,
        prioritizeDuplicates,
        prioritizeSbcStorage,
        playerIds,
        excludedPlayers: excludedPlayersMapped,
        minRating,
        maxRating,
        playersFrom,
    });
});
export const solveSbc = createAsyncThunk("solve-sbc", async (sbcId, { getState, rejectWithValue }) => {
    const { sbcSetsReducer } = getState();
    // const requestId = state.sbcsReducer.currentRequestId;
    const { useUntradeablesOnly, excludeActiveSquad, excludeObjectivePlayers, excludeSbcPlayers, includeTransferlist: includeTransferList, prioritizeDuplicates, prioritizeSbcStorage, minRating, maxRating, } = sbcSetsReducer.filters;
    const { includedPlayers, excludedPlayers } = sbcSetsReducer.filters;
    const includedPlayersMapped = includedPlayers.flatMap((p) => p ? p.resourceId : []);
    const excludedPlayersMapped = excludedPlayers.flatMap((p) => p ? p.resourceId : []);
    try {
        /* Every pool at once, as web solves: the backend runs club, both and
           market for a caller with a club and market alone otherwise, so there
           is no pool to send. `playersFrom` stays in the filters for the swap
           endpoint, which still takes one. */
        const res = await api.solveSbcSolutions(sbcId, {
            untradeable: useUntradeablesOnly,
            excludeActiveSquad,
            excludeObjectivePlayers,
            excludeSbcPlayers,
            includeTransferList,
            prioritizeDuplicates,
            prioritizeSbcStorage,
            includedPlayers: includedPlayersMapped,
            excludedPlayers: excludedPlayersMapped,
            minRating,
            maxRating,
        });
        if (!res.solutions?.length) {
            return rejectWithValue({
                kind: "no-solution",
            });
        }
        // dispatch(usedFeature({ feature: "SOLVE_SBC" }));
        // solve_sbc / solve_sbc_again are tracked from the UI (which knows the
        // subsection + whether it's a re-solve), same as web — see the shared
        // trackSolveSbc* helpers in analytics/events.ts.
        /* Keyed by the first pool that found it. A squad always carries at
           least one tag; the index fallback is only to keep the key a string. */
        const solutions = res.solutions.map((solution, i) => ({
            ...solution,
            id: solution.tags[0] ?? String(i),
        }));
        return { solutions, sbcId }; //, requestId: requestId };
    }
    catch (error) {
        console.error(error);
        if (error.response?.status === 404) {
            return rejectWithValue({
                kind: "no-solution",
                message: error.response?.data?.error?.message,
            });
        }
        if (error.response?.status === 403 &&
            error.response?.data?.error?.message === "ANONYMOUS_SOLVE_LIMIT_REACHED") {
            return rejectWithValue({
                kind: "anonymous-limit",
            });
        }
        return rejectWithValue({ kind: "failure" });
    }
});
const findSet = (state, setId) => state.sbcSets.find((sbcSet) => sbcSet.id === setId);
/**
 * Puts a chosen squad on the challenge itself.
 *
 * The pitch, the player swaps and the hand-off to the web app all read
 * `sbc.solution`, and none of them cares which pool produced it. Mirroring
 * here is what lets the alternatives list switch squads without any of them
 * learning that there is now more than one.
 */
const showSolution = (state, sbcId, solution) => {
    state.selectedSolutionId = solution.id;
    const sbc = state.sbcSets
        .flatMap((sbcSet) => sbcSet.sbcs ?? [])
        .find((entry) => entry.id === sbcId);
    if (!sbc)
        return;
    sbc.solution = {
        ...sbc.solution,
        ...solution,
        // The challenge owns the shape; an entry carries none of its own.
        formation: solution.formation ?? sbc.formation,
    };
};
// Create the slice
const sbcSetsSlice = createSlice({
    name: "sbc-sets",
    initialState,
    reducers: {
        updateSbcFilters: (state, action) => {
            state.filters = action.payload;
        },
        sbcSetFavorited: (state, action) => {
            const sbcSet = findSet(state, action.payload.setId);
            if (sbcSet)
                sbcSet.isFavorite = action.payload.isFavorite;
        },
        sbcSetHidden: (state, action) => {
            const sbcSet = findSet(state, action.payload.setId);
            if (sbcSet)
                sbcSet.isHidden = action.payload.isHidden;
        },
        sbcSetCompleted: (state, action) => {
            const sbcSet = findSet(state, action.payload.setId);
            if (!sbcSet)
                return;
            const { didComplete } = action.payload;
            // Completing a set completes every challenge in it, so there is nothing
            // left to pay for; un-completing puts it back to untouched.
            sbcSet.solvedCount = didComplete ? sbcSet.sbcsCount : 0;
            sbcSet.remainingCost = didComplete ? 0 : null;
            sbcSet.solved = didComplete;
        },
        sbcSetVoted: (state, action) => {
            const sbcSet = findSet(state, action.payload.setId);
            if (!sbcSet)
                return;
            const up = action.payload.vote === "UPVOTE";
            const held = up ? sbcSet.wasUpvoted : sbcSet.wasDownvoted;
            // A vote is single-valued: casting one drops whatever was held before.
            if (sbcSet.wasUpvoted)
                sbcSet.upVotes -= 1;
            if (sbcSet.wasDownvoted)
                sbcSet.downVotes -= 1;
            sbcSet.wasUpvoted = !held && up;
            sbcSet.wasDownvoted = !held && !up;
            if (sbcSet.wasUpvoted)
                sbcSet.upVotes += 1;
            if (sbcSet.wasDownvoted)
                sbcSet.downVotes += 1;
        },
        clearPlayerSwaps: (state) => {
            state.playerSwaps = [];
            state.playerSwapsStatus = "idle";
        },
        // Clears the status/error of the last solve attempt; stored solutions
        // on each sbc are untouched.
        clearSolveAttempt: (state) => {
            state.solveSbcStatus = "idle";
            state.solveSbcError = undefined;
        },
        /** Shows one of the last solve's other squads. */
        selectSbcSolution: (state, action) => {
            const solution = state.solutions.find((entry) => entry.id === action.payload.solutionId);
            if (solution)
                showSolution(state, action.payload.sbcId, solution);
        },
        swapPlayer: (state, action) => {
            const { id, playerIndex, swapIndex } = action.payload;
            const playerSwap = state.playerSwaps[playerIndex]?.[swapIndex];
            if (!playerSwap)
                return;
            state.sbcSets = state.sbcSets.map((sbcSet) => sbcSet.sbcs?.some((sbc) => sbc.id === id)
                ? {
                    ...sbcSet,
                    sbcs: sbcSet.sbcs?.map((sbc) => sbc.id === id
                        ? {
                            ...sbc,
                            solution: sbc.solution && {
                                ...sbc.solution,
                                players: sbc.solution?.players.map((player, index) => index === playerIndex ? playerSwap : player),
                            },
                        }
                        : sbc),
                }
                : sbcSet);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSbcSets.fulfilled, (state, action) => {
            // The list carries no sbcs; a set page loads them with `fetchSbcs`.
            // Keep the ones already loaded, or a list landing while a set page is
            // open (Home and the SBCs screen both fetch it) empties that page.
            const loadedSbcs = new Map(state.sbcSets.flatMap((sbcSet) => sbcSet.sbcs ? [[sbcSet.id, sbcSet.sbcs]] : []));
            state.sbcSets = action.payload.map((sbcSet) => {
                const sbcs = loadedSbcs.get(sbcSet.id);
                return sbcs ? { ...sbcSet, sbcs } : sbcSet;
            });
            state.sbcSetsStatus = "fulfilled";
        })
            .addCase(fetchSbcSets.pending, (state) => {
            // The cached sets stay until the new ones land: every fetch returns
            // the full list, so emptying it only made every list of sets blink.
            state.sbcSetsStatus = "pending";
        })
            .addCase(fetchSbcSets.rejected, (state) => {
            state.sbcSetsStatus = "rejected";
        })
            .addCase(openSbcSet.pending, (state) => {
            state.openSbcSetStatus = "pending";
        })
            .addCase(openSbcSet.fulfilled, (state) => {
            state.openSbcSetStatus = "fulfilled";
        })
            .addCase(openSbcSet.rejected, (state) => {
            state.openSbcSetStatus = "rejected";
        })
            .addCase(fetchSbcSet.fulfilled, (state, action) => {
            // Upsert: the list may arrive later, and a set fetched on its own must
            // not become a duplicate row when it does.
            const known = state.sbcSets.some((sbcSet) => sbcSet.id === action.payload.id);
            state.sbcSets = known
                ? state.sbcSets.map((sbcSet) => sbcSet.id === action.payload.id
                    ? { ...sbcSet, ...action.payload }
                    : sbcSet)
                : [...state.sbcSets, action.payload];
        })
            .addCase(fetchSbcs.fulfilled, (state, action) => {
            state.sbcSets = state.sbcSets.map((sbcSet) => action.payload.some((sbc) => sbc.setId === sbcSet.id)
                ? { ...sbcSet, sbcs: action.payload }
                : sbcSet);
        })
            .addCase(markSbcSolved.fulfilled, (state, action) => {
            state.sbcSets = state.sbcSets.map((sbcSet) => sbcSet.sbcs?.some((sbc) => sbc.id === action.payload.sbcId)
                ? {
                    ...sbcSet,
                    sbcs: sbcSet.sbcs?.map((sbc) => sbc.id === action.payload.sbcId
                        ? { ...sbc, solved: action.payload.solved }
                        : sbc),
                }
                : sbcSet);
        })
            .addCase(solveSbc.fulfilled, (state, action) => {
            const { solutions, sbcId } = action.payload;
            state.solveSbcStatus = "fulfilled";
            state.solutions = solutions;
            // Every solve re-adopts the backend's pick, as the Streamline slice does.
            const recommended = solutions.find((solution) => solution.recommended) ?? solutions[0];
            // The thunk rejects an empty list, so there is always one to show.
            if (recommended)
                showSolution(state, sbcId, recommended);
        })
            .addCase(solveSbc.pending, (state, action) => {
            state.solveSbcStatus = "pending";
            state.solveSbcError = undefined;
            // The previous challenge's squads must not outlive its solve.
            if (state.solutionSbcId !== action.meta.arg) {
                state.solutions = [];
                state.selectedSolutionId = null;
            }
            state.solutionSbcId = action.meta.arg;
        })
            .addCase(solveSbc.rejected, (state, action) => {
            state.solveSbcStatus = "rejected";
            state.solveSbcError = action.payload ?? {
                kind: "failure",
            };
        })
            .addCase(playerSwap.fulfilled, (state, action) => {
            state.playerSwaps = action.payload || [];
            state.playerSwapsStatus = "fulfilled";
        })
            .addCase(playerSwap.pending, (state) => {
            state.playerSwapsStatus = "pending";
        })
            .addCase(playerSwap.rejected, (state) => {
            state.playerSwapsStatus = "rejected";
            // Dropped so a failed refetch can't leave the previous squad's
            // alternatives on screen, indexed against the new squad.
            state.playerSwaps = [];
        });
    },
});
// Export actions
export const { swapPlayer, clearPlayerSwaps, clearSolveAttempt, selectSbcSolution, sbcSetVoted, sbcSetCompleted, sbcSetFavorited, sbcSetHidden, updateSbcFilters, } = sbcSetsSlice.actions;
// Export reducer
export default sbcSetsSlice.reducer;
export const getSbcSetsSelector = (state) => state.sbcSetsReducer;
/* Anything in Customize set away from its default, as web's dot reads it.
   The scope (`playersFrom`) has its own control, so it does not count. */
export const solveSettingsActiveSelector = (state) => {
    const { playersFrom: _scope, ...filters } = state.sbcSetsReducer.filters;
    return Object.keys(filters).some((key) => {
        const value = filters[key];
        return Array.isArray(value)
            ? value.length > 0
            : value !== initialFiltersState[key];
    });
};
/**
 * The solve request's state on its own, so a subscriber that only cares whether
 * a solve landed doesn't re-render on every unrelated change in this slice.
 */
export const solveSbcStatusSelector = (state) => state.sbcSetsReducer.solveSbcStatus;
/** The last solve's squads, and which one the pitch is showing. */
export const sbcSolutionsSelector = (state) => ({
    solutions: state.sbcSetsReducer.solutions,
    selectedSolutionId: state.sbcSetsReducer.selectedSolutionId,
});
/** Whether the set page is still waiting on its first load. */
export const sbcSetLoadingSelector = (state) => state.sbcSetsReducer.openSbcSetStatus === "idle" ||
    state.sbcSetsReducer.openSbcSetStatus === "pending";
export const getSbcSetSelector = (setId) => (state) => {
    return state.sbcSetsReducer.sbcSets.find((sbcSet) => sbcSet.id === setId);
};
export const getSbcSelector = (sbcId) => (state) => {
    const sbcSet = state.sbcSetsReducer.sbcSets.find((sbcSet) => sbcSet.sbcs?.find((sbc) => sbc.id === sbcId));
    return sbcSet?.sbcs?.find((sbc) => sbc.id === sbcId);
};
//# sourceMappingURL=sbcSetsSlice.js.map